import React from 'react';
import { Eye, Heart, Film, Clock, User, Hash } from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty, StatusBadge, getGenericStatusVariant } from './shared';
import { useUserContentEngagement } from '../../hooks/useUserTabs';
import { formatNumber, formatRelativeTime, formatDateTime } from '../../utils/helpers';
import type { ContentEngagementView, ContentEngagementLike } from '../../types/userTabs';

interface EngagementTabProps {
  userId: number;
}

const ViewRow: React.FC<{ view: ContentEngagementView }> = ({ view }) => {
  const content = view.content;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/30 last:border-0">
      <div className="p-1.5 rounded-lg bg-blue-500/10 flex-shrink-0 mt-0.5">
        <Film className="h-3.5 w-3.5 text-blue-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground line-clamp-2">
          {content?.caption || <span className="text-muted-foreground/50 font-mono text-xs">{view.contentId}</span>}
        </p>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-muted-foreground">
          {content?.owner?.name && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {content.owner.name}
            </span>
          )}
          {content?.type && (
            <span className="uppercase">{content.type}</span>
          )}
          {content?.status && (
            <StatusBadge
              status={content.status.replace(/_/g, ' ')}
              variant={getGenericStatusVariant(content.status)}
            />
          )}
          <span className="flex items-center gap-1" title={formatDateTime(view.viewedAt)}>
            <Clock className="h-3 w-3" />
            {formatRelativeTime(view.viewedAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

const LikeRow: React.FC<{ like: ContentEngagementLike }> = ({ like }) => {
  const asset = like.asset;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/30 last:border-0">
      <div className="p-1.5 rounded-lg bg-rose-500/10 flex-shrink-0 mt-0.5">
        <Heart className="h-3.5 w-3.5 text-rose-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground line-clamp-2">
          {asset?.caption || <span className="text-muted-foreground/50 font-mono text-xs">{like.assetId}</span>}
        </p>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-muted-foreground">
          {asset?.owner?.name && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {asset.owner.name}
            </span>
          )}
          {asset?.type && (
            <span className="uppercase">{asset.type}</span>
          )}
          {asset?.status && (
            <StatusBadge
              status={asset.status.replace(/_/g, ' ')}
              variant={getGenericStatusVariant(asset.status)}
            />
          )}
          <span className="flex items-center gap-1" title={formatDateTime(like.createdAt)}>
            <Clock className="h-3 w-3" />
            {formatRelativeTime(like.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

const EngagementTab: React.FC<EngagementTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserContentEngagement(userId, true);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No engagement data available" />;

  const { viewCount, likeCount, recentViews, recentLikes } = data;

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <MetricCard
          label="Total Views"
          value={formatNumber(viewCount)}
          icon={Eye}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Total Likes"
          value={formatNumber(likeCount)}
          icon={Heart}
          color="text-rose-400"
          bgColor="bg-rose-500/10"
        />
      </div>

      {/* Recent Views */}
      <SectionCard
        title="Recent Content Views"
        icon={Eye}
        subtitle={`${recentViews.length} recent view${recentViews.length !== 1 ? 's' : ''}`}
      >
        {recentViews.length > 0 ? (
          <div>
            {recentViews.map((view) => (
              <ViewRow key={view.id} view={view} />
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Eye className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No recent views</p>
          </div>
        )}
      </SectionCard>

      {/* Recent Likes */}
      <SectionCard
        title="Recent Content Likes"
        icon={Heart}
        subtitle={`${recentLikes.length} recent like${recentLikes.length !== 1 ? 's' : ''}`}
      >
        {recentLikes.length > 0 ? (
          <div>
            {recentLikes.map((like) => (
              <LikeRow key={like.id} like={like} />
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Heart className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No recent likes</p>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default EngagementTab;
